import React from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  X,
} from 'lucide-react';

import { Button } from '../../ui';
import { useWebTestAgentModal } from './useWebTestAgentModal';
import type { WebTestAgentModalProps } from './webTestAgentModalTypes';

// LT-520 — Test Agent (WebRTC) modal. Visually mirrors TestPromptModal so
// the operator's mental model carries over from the PSTN test flow, but
// replaces the phone-input + status-polling chrome with mic permission,
// LiveKit audio, mute, and duration-cap awareness.
const WebTestAgentModal: React.FC<WebTestAgentModalProps> = ({
  isOpen,
  onClose,
  campaignId,
}) => {
  const {
    phase,
    error,
    callDuration,
    secondsRemaining,
    micPermissionDenied,
    isMuted,
    showWarning,
    resolvedVoiceId,
    creditsDeducted,
    startCall,
    toggleMute,
    endCall,
    handleClose,
    formatDuration,
  } = useWebTestAgentModal(isOpen, onClose, campaignId);

  if (!isOpen) return null;

  const isConnecting = phase === 'connecting';
  const isActive = phase === 'active';
  const isEnding = phase === 'ending';
  const isEnded = phase === 'ended';
  const isError = phase === 'error';

  const statusLabel = (() => {
    if (isConnecting) return 'Connecting…';
    if (isActive) return 'Talking with the agent';
    if (isEnding) return 'Ending call…';
    if (isEnded) return 'Call ended';
    return 'Ready to start';
  })();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 pt-20 pb-8 px-4 overflow-y-auto flex items-center justify-center min-h-full">
      <div className="bg-slate-900/98 backdrop-blur-xl rounded-2xl border border-slate-700/40 w-full max-w-lg h-[82vh] min-h-[420px] max-h-[640px] overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] flex flex-col flex-shrink-0">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/40 bg-slate-900/30 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold tracking-tight text-white">Test Agent</h2>
          <button
            onClick={handleClose}
            className="p-2 -m-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
            aria-label="Close test agent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 pt-8 pb-5 sm:px-8 sm:pt-10 sm:pb-6 bg-slate-900/30 custom-scrollbar flex flex-col items-center">

            {/* Error / mic permission */}
            {micPermissionDenied ? (
              <div className="w-full max-w-sm rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 mb-5">
                <div className="flex items-start gap-2.5">
                  <MicOff className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-amber-200 font-medium text-sm mb-1">
                      Microphone access blocked
                    </h3>
                    <p className="text-amber-100/90 text-xs leading-relaxed">
                      Allow microphone in your browser&apos;s site permissions and
                      retry — the test call uses your mic for the conversation.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
            {isError && !micPermissionDenied && error ? (
              <div className="w-full max-w-sm rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 mb-5">
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-amber-200 font-medium text-sm mb-1">
                      Couldn&apos;t start call
                    </h3>
                    <p className="text-amber-100/90 text-xs break-words">{error}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Pre-call / ended description + voice line */}
            {(phase === 'idle' || isEnded || isError) ? (
              <div className="w-full max-w-sm text-center mb-4">
                <p className="text-sm text-slate-300 mb-2">
                  Talk to this campaign&apos;s agent right in your browser. No phone
                  number needed.
                </p>
                {resolvedVoiceId ? (
                  <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                    voice: {resolvedVoiceId}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* Mic activity indicator while active */}
            {isActive ? (
              <div className="flex flex-col items-center mb-3">
                <div
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
                    isMuted
                      ? 'bg-slate-700/50 border border-slate-600/60'
                      : 'bg-cyan-500/15 border border-cyan-400/40 shadow-[0_0_24px_rgba(6,182,212,0.25)]'
                  }`}
                >
                  {isMuted ? (
                    <MicOff className="w-8 h-8 text-slate-400" />
                  ) : (
                    <Mic className="w-8 h-8 text-cyan-300" />
                  )}
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  {isMuted ? 'Mic muted' : 'Speak now — the agent is listening.'}
                </p>
              </div>
            ) : null}

            {/* Ended summary */}
            {isEnded ? (
              <div className="flex flex-col items-center justify-center py-2.5 px-4 mb-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="bg-slate-800/90 border border-slate-600/60 rounded-lg px-4 py-1.5 flex items-center gap-2">
                  <PhoneOff className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-white text-xs font-medium">Call ended</span>
                  <span className="text-slate-400 text-xs">
                    {formatDuration(callDuration)}
                  </span>
                </div>
                {creditsDeducted !== null ? (
                  <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                    {creditsDeducted === 0
                      ? 'No credits deducted.'
                      : `${creditsDeducted} credit${creditsDeducted === 1 ? '' : 's'} deducted.`}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* Warning banner at < 30s remaining */}
            {showWarning && secondsRemaining !== null ? (
              <div className="w-full max-w-sm mb-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs text-center">
                {secondsRemaining}s remaining — call will auto-disconnect.
              </div>
            ) : null}

            {/* Primary action button */}
            <div className="min-h-[44px] flex items-center justify-center">
              {(phase === 'idle' || isEnded || isError) ? (
                <Button
                  intent="primary"
                  type="button"
                  onClick={startCall}
                  disabled={isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Connecting…</span>
                    </>
                  ) : (
                    <>
                      <Mic size={14} />
                      <span>{isEnded ? 'Start another call' : 'Start talking'}</span>
                    </>
                  )}
                </Button>
              ) : null}
              {isActive ? (
                <div className="flex items-center gap-2">
                  <Button intent="secondary" type="button" onClick={toggleMute}>
                    {isMuted ? <Mic size={14} /> : <MicOff size={14} />}
                    <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                  </Button>
                  <Button intent="danger" type="button" onClick={endCall}>
                    <PhoneOff size={14} />
                    <span>End call</span>
                  </Button>
                </div>
              ) : null}
              {isEnding ? (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Ending…</span>
                </div>
              ) : null}
            </div>

            {/* How-to box (idle / error state only) */}
            {(phase === 'idle' || isError) ? (
              <div className="w-full max-w-sm mt-5 rounded-xl bg-slate-800/40 border border-slate-700/40 p-5">
                <h3 className="text-slate-200 font-medium text-xs uppercase tracking-wider mb-2">
                  How it works
                </h3>
                <ol className="list-decimal list-inside space-y-1 text-xs text-slate-400 leading-relaxed">
                  <li>Click &quot;Start talking&quot; and allow your browser microphone.</li>
                  <li>Speak with the agent like a real call.</li>
                  <li>Click &quot;End call&quot; to finish — credits deduct by duration.</li>
                  <li>Up to 5 minutes per test session.</li>
                </ol>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer status bar */}
        <div className="flex-shrink-0 px-6 py-3 bg-slate-800/80 border-t border-slate-700/40 flex items-center gap-3 rounded-b-2xl">
          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              isActive
                ? 'bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.5)]'
                : 'bg-slate-500'
            }`}
          />
          <span className="text-slate-200 text-sm font-medium">{statusLabel}</span>
          {(isActive || isEnded || isEnding) ? (
            <div className="flex items-center gap-1.5 text-slate-400 ml-auto">
              <Clock className="w-3.5 h-3.5" />
              <span className="text-slate-300 text-sm tabular-nums">
                {formatDuration(callDuration)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WebTestAgentModal;
